import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { AnnouncementsListComponent } from 'shared/components/announcements/announcements-list.component';
import { SettingsService } from 'shared/services/settings.service';
import { UserSettings, UserSubscription } from 'shared/models/user-settings.model';
import { Subscription, take, finalize } from 'rxjs';
import { DateTime } from 'luxon';
import { LuxonDatePipe } from 'shared/pipes/luxon-date.pipe';
import { AdminTileComponent } from '../management/components/admin-tile.component';
import { MarkoutsApiService } from 'shared/services/api/markouts.api.service';
import { ProjectsApiService } from 'shared/services/api/projects.api.service';
import { APIResponse } from 'shared/models/api-response';
import { Markout } from 'shared/models/markout.model';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WeatherData, WeatherService } from 'shared/services/weather.service';
import { DialogService } from 'shared/services/dialog.service';
import { SnackbarService } from 'shared/services/snackbar.service';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MarkoutPreviewDialogComponent } from 'shared/dialogs/markout-preview-dialog/markout-preview-dialog.component';
import { DarkShadowDirective } from 'shared/directives/dark-shadow.directive';
import { DashboardApiService } from 'shared/services/api/dashboard.api.service';
import { DashboardCounters } from 'shared/models/dashboard-counters';

@Component({
  standalone: true,
  imports: [CommonModule, LuxonDatePipe, RouterLink, AnnouncementsListComponent, MatIconModule, MatButtonModule, AdminTileComponent, MatProgressSpinnerModule, DarkShadowDirective],
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {

  private _subscriptions: Subscription[] = []

  public greeting: string = 'Hello';
  public userSettings: UserSettings = new UserSettings()
  public currentDate: Date = new Date();

  subscription: UserSubscription | null = null
  daysUntilExpiry: number | null = null;

  // Markout counters
  activeCount = 0;
  expiringCount = 0;
  pendingCount = 0;
  expiredCount = 0;
  pendingMarkoutRequestsCount = 0;

  // Markout status → projects mapping
  expandedMarkoutStatus: string | null = null;
  expandedProjectId: string | null = null;
  confirmingDeleteId: string | null = null;
  projectsByMarkoutStatus: { [status: string]: { _id: string, name: string, city?: string, state?: string, count: number }[] } = {};
  markoutsByStatusAndProject: { [key: string]: Markout[] } = {};

  // Weather
  weather: WeatherData | null = null;
  weatherIcon: string = 'tabler_outline:cloud';

  // Projects expandable sections
  activeTab: 'projects' | 'markouts' | null = null;
  projects: any[] = [];
  isLoadingProjects = false;
  selectedProjectId: string | null = null;
  private projectsLoaded = false;

  // Deleted projects
  deletedProjects: any[] = [];
  showDeleted = false;
  isLoadingDeleted = false;
  
  // Permissions
  hasPrintProjects = false;
  hasViewProjects = false;
  hasCreateProject = false;
  hasViewMarkouts = false;
  hasCreateMarkout = false;
  hasDeleteMarkout = false;
  hasProcessProjectRequest = false;
  hasProcessMarkoutRequest = false;
  hasCreateTimesheet = false;
  hasViewFieldMeasure = false;

  constructor(
    private _settingsService: SettingsService,
    private _markoutsApi: MarkoutsApiService,
    private _projectsApi: ProjectsApiService,
    private _weatherService: WeatherService,
    public _dialogService: DialogService,
    private _snackbarService: SnackbarService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _dialog: MatDialog,
    private _dashboardApiService: DashboardApiService
  ) { }

  ngOnInit(): void {

    this._setGreeting();

    this._subscriptions.push(
      this._settingsService.currentUserSettings.subscribe({
        next: (userSettings: UserSettings) => {
          this.userSettings = userSettings
          this.subscription = userSettings.subscription
          this.calculateDaysUntilExpiry();
          const role = userSettings.user?.role || 'user';
          const permissions = userSettings.user?.permissions || [];
          const isOwner = role === 'user';
          this.hasPrintProjects = isOwner || permissions.includes('PRINT_PROJECTS');
          this.hasViewProjects = isOwner || permissions.includes('VIEW_PROJECT_DETAILS');
          this.hasCreateProject = isOwner || permissions.includes('CREATE_PROJECT');
          this.hasViewMarkouts = isOwner || permissions.includes('VIEW_MARKOUT');
          this.hasCreateMarkout = isOwner || permissions.includes('CREATE_MARKOUT');
          this.hasDeleteMarkout = isOwner || permissions.includes('DELETE_MARKOUT');
          this.hasProcessProjectRequest = isOwner || permissions.includes('PROCESS_PROJECT_REQUEST');
          this.hasProcessMarkoutRequest = isOwner || (permissions.includes('PROCESS_MARKOUT_REQUEST') || permissions.includes('DELETE_MARKOUT_REQUEST'));
          this.hasCreateTimesheet = isOwner || permissions.includes('CREATE_SHEET') || permissions.includes('CREATE_TIMESHEET');
          this.hasViewFieldMeasure = isOwner || permissions.includes('VIEW_FIELD_MEASURE');

          // Fallback tab selection if the user doesn't have permissions for the active/saved tab
          if (this.activeTab === 'projects' && !this.hasViewProjects) {
            this.activeTab = this.hasViewMarkouts ? 'markouts' : null;
          } else if (this.activeTab === 'markouts' && !this.hasViewMarkouts) {
            this.activeTab = this.hasViewProjects ? 'projects' : null;
          } else if (this.activeTab === null) {
            if (this.hasViewProjects && !this.hasViewMarkouts) {
              this.activeTab = 'projects';
            } else if (this.hasViewMarkouts && !this.hasViewProjects) {
              this.activeTab = 'markouts';
            }
          }
        }
      })
    )

    this._subscriptions.push(
      this._route.queryParams.subscribe(params => {
        if (params['tab'] === 'markouts') {
          if (this.hasViewMarkouts) {
            this.activeTab = 'markouts';
            localStorage.setItem('dashboard_active_tab', 'markouts');
          }
        } else {
          const saved = localStorage.getItem('dashboard_active_tab');
          if (saved === 'projects' && this.hasViewProjects) {
            this.activeTab = 'projects';
          } else if (saved === 'markouts' && this.hasViewMarkouts) {
            this.activeTab = 'markouts';
          }
        }
      })
    );

    this._subscriptions.push(
      this._dashboardApiService.counters$.subscribe({
        next: (counters: DashboardCounters) => {
          this.pendingMarkoutRequestsCount = counters.pendingMarkoutRequestsCount || 0;
        }
      })
    )

    this.loadMarkoutCounts();
    this.loadWeather();
    this.loadProjectsIfNeeded();
  }

  private loadMarkoutCounts(): void {
    this._markoutsApi.get({}).subscribe({
      next: (response: APIResponse) => {
        if (response.success && response.data) {
          const markouts: Markout[] = response.data.map((m: any) => new Markout(m));

          const statusMap: { [status: string]: Map<string, { _id: string, name: string, city?: string, state?: string, count: number }> } = {
            ACTIVE: new Map(),
            EXPIRING: new Map(),
            PENDING: new Map(),
            EXPIRED: new Map()
          };

          for (const m of markouts) {
            switch (m.status) {
              case 'ACTIVE': this.activeCount++; break;
              case 'EXPIRING': this.expiringCount++; break;
              case 'PENDING': this.pendingCount++; break;
              case 'EXPIRED': this.expiredCount++; break;
            }

            if (m.projectId && statusMap[m.status]) {
              const key = `${m.status}_${m.projectId}`;
              if (!this.markoutsByStatusAndProject[key]) {
                this.markoutsByStatusAndProject[key] = [];
              }
              this.markoutsByStatusAndProject[key].push(m);

              const existing = statusMap[m.status].get(m.projectId);
              if (existing) {
                existing.count++;
              } else {
                statusMap[m.status].set(m.projectId, {
                  _id: m.projectId,
                  name: m.project?.name || 'Unknown Project',
                  city: m.project?.address?.city,
                  state: m.project?.address?.state,
                  count: 1
                });
              }
            }
          }

          for (const status of Object.keys(statusMap)) {
            this.projectsByMarkoutStatus[status] = Array.from(statusMap[status].values());
          }
        }
      }
    });
  }

  toggleMarkoutStatus(status: string): void {
    this.expandedMarkoutStatus = this.expandedMarkoutStatus === status ? null : status;
    this.expandedProjectId = null;
    this.confirmingDeleteId = null;
    if (status === 'NEW' && this.expandedMarkoutStatus === 'NEW') {
      this.loadProjectsIfNeeded();
    }
  }

  toggleProject(projectId: string): void {
    this.expandedProjectId = this.expandedProjectId === projectId ? null : projectId;
    this.confirmingDeleteId = null;
  }

  getMarkoutsForProject(status: string, projectId: string): Markout[] {
    return this.markoutsByStatusAndProject[`${status}_${projectId}`] || [];
  }

  viewMarkout(markout: Markout): void {
    this._dialog.open(MarkoutPreviewDialogComponent, {
      width: '340px',
      maxHeight: '85vh',
      data: { markout }
    });
  }

  confirmDelete(markoutId: string): void {
    this.confirmingDeleteId = this.confirmingDeleteId === markoutId ? null : markoutId;
  }

  deleteMarkout(markout: Markout): void {
    this._markoutsApi.delete(markout._id).pipe(take(1)).subscribe({
      next: (res: APIResponse) => {
        if (res.success) {
          // Remove from local arrays
          const key = `${markout.status}_${markout.projectId}`;
          this.markoutsByStatusAndProject[key] = (this.markoutsByStatusAndProject[key] || []).filter(m => m._id !== markout._id);

          // Update project count
          const projects = this.projectsByMarkoutStatus[markout.status] || [];
          const proj = projects.find(p => p._id === markout.projectId);
          if (proj) {
            proj.count--;
            if (proj.count <= 0) {
              this.projectsByMarkoutStatus[markout.status] = projects.filter(p => p._id !== markout.projectId);
              this.expandedProjectId = null;
            }
          }

          // Update status counter
          switch (markout.status) {
            case 'ACTIVE': this.activeCount--; break;
            case 'EXPIRING': this.expiringCount--; break;
            case 'PENDING': this.pendingCount--; break;
            case 'EXPIRED': this.expiredCount--; break;
          }

          this.confirmingDeleteId = null;
          this._dashboardApiService.getCounters().pipe(take(1)).subscribe();
        }
      }
    });
  }

  toggleTab(tab: 'projects' | 'markouts'): void {
    this.activeTab = this.activeTab === tab ? null : tab;
    localStorage.setItem('dashboard_active_tab', String(this.activeTab));
    this.selectedProjectId = null;
    if (this.activeTab === 'projects') {
      this.loadProjectsIfNeeded();
    }
  }

  selectProject(projectId: string): void {
    this.selectedProjectId = this.selectedProjectId === projectId ? null : projectId;
  }



  private loadProjectsIfNeeded(): void {
    if (this.projectsLoaded) return;
    this.isLoadingProjects = true;
    this._projectsApi.get({ currentPage: '1', itemsPerPage: '500' }).subscribe({
      next: (res: any) => {
        if (res.success && res.data?.results) {
          this.projects = res.data.results;
        }
        this.projectsLoaded = true;
        this.isLoadingProjects = false;
      },
      error: () => {
        this.isLoadingProjects = false;
      }
    });
  }

  toggleDeletedProjects(): void {
    this.showDeleted = !this.showDeleted;
    if (this.showDeleted && this.deletedProjects.length === 0) {
      this.loadDeletedProjects();
    }
  }

  loadDeletedProjects(): void {
    this.isLoadingDeleted = true;
    this._projectsApi.getDeleted().subscribe({
      next: (response: any) => {
        if (response.success) {
          this.deletedProjects = response.data;
        }
        this.isLoadingDeleted = false;
      },
      error: () => {
        this._snackbarService.showSnackBar('Failed to load deleted projects', 'error');
        this.isLoadingDeleted = false;
      }
    });
  }

  restoreProject(id: string): void {
    this._projectsApi.restore(id).subscribe({
      next: (response: any) => {
        if (response.success) {
          this._snackbarService.showSnackBar('Project restored successfully', 'success');
          this.deletedProjects = this.deletedProjects.filter(p => p._id !== id);
          this.projectsLoaded = false;
          this.loadProjectsIfNeeded();
        }
      },
      error: () => {
        this._snackbarService.showSnackBar('Failed to restore project', 'error');
      }
    });
  }

  private calculateDaysUntilExpiry(): void {
    if (this.subscription?.subscriptionStatus === 'active' && this.subscription?.subscriptionCurrentPeriodEnd) {
      const now = DateTime.now().startOf('day');
      const endDate = DateTime.fromISO(this.subscription.subscriptionCurrentPeriodEnd as any).startOf('day');
      
      if (endDate.isValid) {
        const diff = endDate.diff(now, 'days').toObject();
        this.daysUntilExpiry = Math.floor(diff.days ?? -1);
      }
    }
  }

  ngOnDestroy(): void {
    localStorage.removeItem('dashboard_active_tab');
    this._subscriptions.forEach(sub => sub.unsubscribe())
  }

  private _setGreeting(): void {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      this.greeting = 'Good morning';
    } else if (hour >= 12 && hour < 17) {
      this.greeting = 'Good afternoon';
    } else if (hour >= 17 && hour < 22) {
      this.greeting = 'Good evening';
    } else {
      this.greeting = 'Hello';
    }
  }

  private loadWeather(): void {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        this._weatherService.getWeatherByCoords(latitude, longitude).subscribe({
          next: (data) => {
            this.weather = data;
            if (data) {
              this.weatherIcon = this.getWeatherIcon(data.weatherCondition);
            }
          }
        });
      },
      () => {
        // Geolocation denied/unavailable — fall back to company zip
        const zip = this.userSettings.company?.postalCode;
        const state = this.userSettings.company?.state;
        if (!zip || !state) return;

        this._weatherService.getWeatherByZip(zip, state).subscribe({
          next: (data) => {
            this.weather = data;
            if (data) {
              this.weatherIcon = this.getWeatherIcon(data.weatherCondition);
            }
          }
        });
      }
    );
  }

  printProjectList(): void {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      this._snackbarService.showSnackBar('Pop-up blocked. Please allow pop-ups to print the project list.', 'error');
      return;
    }

    const projectsListHtml = this.projects
      .map((project, index) => {
        const addressStr = [
          project.address?.streetNumber,
          project.address?.street,
          project.address?.city,
          project.address?.state,
          project.address?.zipCode
        ].filter(Boolean).join(' ');

        let formattedBudget = '—';
        if (project.budget) {
          const num = parseFloat(String(project.budget).replace(/[^0-9.]/g, ''));
          if (!isNaN(num)) {
            formattedBudget = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
          } else {
            formattedBudget = String(project.budget);
          }
        }

        return `
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px 8px; font-weight: 600; color: #1e293b;">${index + 1}</td>
            <td style="padding: 12px 8px; font-weight: 500; color: #0f172a;">${this.escapeHtml(project.name)}</td>
            <td style="padding: 12px 8px; color: #334155;">${this.escapeHtml(project.projectNumber || '—')}</td>
            <td style="padding: 12px 8px; color: #334155;">${this.escapeHtml(project.clientName || '—')}</td>
            <td style="padding: 12px 8px; color: #475569; font-size: 0.875rem;">${this.escapeHtml(addressStr || '—')}</td>
            <td style="padding: 12px 8px; text-align: right; color: #334155; font-weight: 600;">${this.escapeHtml(formattedBudget)}</td>
            <td style="padding: 12px 8px; text-align: center;">
              <span style="display: inline-block; padding: 2px 8px; font-size: 0.75rem; font-weight: 700; border-radius: 9999px; background-color: ${project.status === 'ACTIVE' ? '#dcfce7' : '#f3f4f6'}; color: ${project.status === 'ACTIVE' ? '#166534' : '#374151'}; text-transform: uppercase;">
                ${this.escapeHtml(project.status || 'ACTIVE')}
              </span>
            </td>
          </tr>
        `;
      })
      .join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Project List - ${this.userSettings.company?.name || 'Markout Pro'}</title>
        <meta charset="utf-8">
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            margin: 0;
            padding: 40px 24px;
            color: #334155;
            background-color: #ffffff;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 2px solid #cbd5e1;
            padding-bottom: 16px;
            margin-bottom: 24px;
          }
          .title {
            margin: 0;
            font-size: 24px;
            font-weight: 800;
            color: #0f172a;
          }
          .subtitle {
            margin: 4px 0 0 0;
            font-size: 14px;
            color: #64748b;
          }
          .meta {
            text-align: right;
            font-size: 12px;
            color: #64748b;
          }
          .meta p {
            margin: 2px 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            text-align: left;
            font-size: 14px;
          }
          th {
            background-color: #f8fafc;
            color: #475569;
            font-weight: 700;
            text-transform: uppercase;
            font-size: 11px;
            letter-spacing: 0.05em;
            padding: 10px 8px;
            border-bottom: 2px solid #e2e8f0;
          }
          @media print {
            body {
              padding: 0;
            }
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1 class="title">Project List</h1>
            <p class="subtitle">${this.escapeHtml(this.userSettings.company?.name || 'Markout Pro')}</p>
          </div>
          <div class="meta">
            <p><strong>Total Projects:</strong> ${this.projects.length}</p>
            <p><strong>Date Printed:</strong> ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th>Project Name</th>
              <th>Project #</th>
              <th>Client</th>
              <th>Address</th>
              <th style="text-align: right;">Contract Amount</th>
              <th style="text-align: center; width: 80px;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${projectsListHtml || '<tr><td colspan="7" style="padding: 24px; text-align: center; color: #64748b;">No projects found.</td></tr>'}
          </tbody>
        </table>
        <script>
          window.addEventListener('DOMContentLoaded', () => {
            setTimeout(() => {
              window.print();
              window.close();
            }, 500);
          });
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }

  escapeHtml(text: string): string {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private getWeatherIcon(condition: string): string {
    switch (condition) {
      case 'Sunny': return 'tabler_outline:sun';
      case 'Cloudy': return 'tabler_outline:cloud';
      case 'Rain': return 'tabler_outline:cloud-rain';
      case 'Snow': return 'tabler_outline:snowflake';
      default: return 'tabler_outline:cloud';
    }
  }
  
}
